import React, { useState } from "react";
import {
  Box,
  Avatar,
  Typography,
  useTheme,
  alpha,
  Checkbox,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { Draggable } from "@hello-pangea/dnd";
import { AddModerator, RemoveModerator, AdminPanelSettings, SupervisorAccount } from "@mui/icons-material";
import { useTranslate } from "@refinedev/core";

export interface DnDUser {
  id: number;
  fullname: string | null;
  email: string;
  profilePicture: string | null;
  role: string;
  subGroupId: number | null;
  subGroup?: { id: number; name: string } | null;
}

interface DnDUserCardProps {
  user: DnDUser;
  index: number;
  isSelected: boolean;
  selectedCount: number;
  onSelect: (userId: number, event: React.MouseEvent) => void;
  onPromote?: (userId: number) => Promise<void>;
  onDemote?: (userId: number) => Promise<void>;
  showRoleActions?: boolean;
}

export const DnDUserCard: React.FC<DnDUserCardProps> = ({
  user,
  index,
  isSelected,
  selectedCount,
  onSelect,
  onPromote,
  onDemote,
  showRoleActions = false,
}) => {
  const theme = useTheme();
  const translate = useTranslate();
  const isSubModerator = user.role === "SUB_MODERATOR";
  const isModerator = user.role === "MODERATOR" || user.role === "MODERATOR_FREE";
  const isHigherRole = ["ADMIN", "SAFEE_MODERATOR", "MODERATOR", "MODERATOR_FREE"].includes(user.role);
  const hasSpecialRole = isSubModerator || isModerator;

  const [actionLoading, setActionLoading] = useState(false);

  const handlePromote = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!onPromote) return;
    setActionLoading(true);
    try {
      await onPromote(user.id);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDemote = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!onDemote) return;
    setActionLoading(true);
    try {
      await onDemote(user.id);
    } finally {
      setActionLoading(false);
    }
  };

  // Only show role actions for users in a sub-group and not higher roles
  const canShowRoleActions = showRoleActions && user.subGroupId !== null && !isHigherRole;

  return (
    <Draggable draggableId={`user-${user.id}`} index={index}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={(e) => onSelect(user.id, e)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            mb: 1,
            borderRadius: 2,
            bgcolor: snapshot.isDragging
              ? alpha(theme.palette.primary.main, 0.15)
              : isSelected
              ? alpha(theme.palette.primary.main, 0.08)
              : isModerator
              ? alpha(theme.palette.success.main, 0.04)
              : isSubModerator
              ? alpha(theme.palette.warning.main, 0.04)
              : theme.palette.background.paper,
            border: `1px solid ${
              snapshot.isDragging
                ? theme.palette.primary.main
                : isSelected
                ? alpha(theme.palette.primary.main, 0.5)
                : isModerator
                ? alpha(theme.palette.success.main, 0.4)
                : isSubModerator
                ? alpha(theme.palette.warning.main, 0.4)
                : alpha(theme.palette.divider, 0.3)
            }`,
            boxShadow: snapshot.isDragging
              ? theme.shadows[8]
              : isSelected
              ? theme.shadows[2]
              : isModerator
              ? `0 1px 4px ${alpha(theme.palette.success.main, 0.15)}`
              : isSubModerator
              ? `0 1px 4px ${alpha(theme.palette.warning.main, 0.15)}`
              : "none",
            cursor: "grab",
            transition: "all 0.15s ease",
            "&:hover": {
              bgcolor: isModerator
                ? alpha(theme.palette.success.main, 0.08)
                : isSubModerator
                ? alpha(theme.palette.warning.main, 0.08)
                : alpha(theme.palette.primary.main, 0.04),
              borderColor: isModerator
                ? alpha(theme.palette.success.main, 0.6)
                : isSubModerator
                ? alpha(theme.palette.warning.main, 0.6)
                : alpha(theme.palette.primary.main, 0.3),
              "& .role-action-button": {
                opacity: 1,
              },
            },
            position: "relative",
            // Show count badge when dragging multiple selected items
            ...(snapshot.isDragging &&
              isSelected &&
              selectedCount > 1 && {
                "&::after": {
                  content: `"${selectedCount}"`,
                  position: "absolute",
                  top: -8,
                  right: -8,
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                },
              }),
          }}
        >
          <Checkbox
            checked={isSelected}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              // Simulate Ctrl+click to toggle selection
              onSelect(user.id, { ...e, ctrlKey: true } as React.MouseEvent);
            }}
            sx={{
              p: 0,
              color: alpha(theme.palette.divider, 0.5),
              "&.Mui-checked": {
                color: theme.palette.primary.main,
              },
            }}
          />

          <Avatar
            src={user.profilePicture || undefined}
            sx={{
              width: 36,
              height: 36,
              bgcolor: isModerator
                ? alpha(theme.palette.success.main, 0.2)
                : isSubModerator
                ? alpha(theme.palette.warning.main, 0.2)
                : alpha(theme.palette.primary.main, 0.1),
              color: isModerator
                ? theme.palette.success.dark
                : isSubModerator
                ? theme.palette.warning.dark
                : theme.palette.primary.main,
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {user.fullname?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography
                variant="body2"
                fontWeight={hasSpecialRole ? 600 : 500}
                noWrap
                sx={{
                  lineHeight: 1.3,
                  color: isModerator
                    ? theme.palette.success.dark
                    : isSubModerator
                    ? theme.palette.warning.dark
                    : "text.primary",
                }}
              >
                {user.fullname || user.email || "-"}
              </Typography>
              {isModerator && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.success.main, 0.15),
                    color: theme.palette.success.dark,
                    fontWeight: 600,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {translate("users.roles.moderator", "Modérateur")}
                </Typography>
              )}
              {isSubModerator && (
                <Typography
                  variant="caption"
                  sx={{
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.warning.main, 0.15),
                    color: theme.palette.warning.dark,
                    fontWeight: 600,
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {translate("users.subGroup.moderator", "Responsable")}
                </Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: "block" }}
            >
              {user.email}
            </Typography>
          </Box>

          {/* Role action icons */}
          {canShowRoleActions && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* Action button - visible on hover */}
              <Tooltip
                title={
                  isSubModerator
                    ? translate("subGroups.demoteFromSubModerator", "Retirer le rôle de responsable")
                    : translate("subGroups.promoteToSubModerator", "Promouvoir responsable")
                }
                arrow
              >
                <IconButton
                  className="role-action-button"
                  size="small"
                  onClick={isSubModerator ? handleDemote : handlePromote}
                  disabled={actionLoading}
                  sx={{
                    opacity: 0,
                    transition: "opacity 0.15s ease",
                    bgcolor: isSubModerator
                      ? alpha(theme.palette.error.main, 0.08)
                      : alpha(theme.palette.success.main, 0.08),
                    color: isSubModerator
                      ? theme.palette.error.main
                      : theme.palette.success.main,
                    "&:hover": {
                      bgcolor: isSubModerator
                        ? alpha(theme.palette.error.main, 0.15)
                        : alpha(theme.palette.success.main, 0.15),
                    },
                    "&.Mui-disabled": {
                      opacity: 1,
                      bgcolor: alpha(theme.palette.action.disabled, 0.1),
                    },
                  }}
                >
                  {actionLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : isSubModerator ? (
                    <RemoveModerator fontSize="small" />
                  ) : (
                    <AddModerator fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              {/* Shield icon for SUB_MODERATOR - always visible */}
              {isSubModerator && (
                <Tooltip title={translate("users.subGroup.moderator", "Responsable")} arrow>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.warning.main, 0.12),
                      color: theme.palette.warning.main,
                    }}
                  >
                    <AdminPanelSettings fontSize="small" />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}

          {/* Icon for MODERATOR - always visible */}
          {isModerator && (
            <Tooltip title={translate("users.roles.moderator", "Modérateur")} arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.main,
                }}
              >
                <SupervisorAccount fontSize="small" />
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </Draggable>
  );
};

export default DnDUserCard;
